import React, { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PlaylistAdd } from "@mui/icons-material";
import { Box } from "@mui/material";
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IRowNode,
  ModelUpdatedEvent,
  SelectionChangedEvent,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-community";
import { useSnackbar } from "notistack";

import analytics from "fond/analytics";
import { AttachmentBaseEventProps } from "fond/analytics/events/attachments";
import { selectAllVersionLayers } from "fond/api";
import { DEFAULT_PAGE_LIMIT } from "fond/constants";
import { downloadAttachments } from "fond/redux/attachments";
import { AppThunkDispatch, Attachment, AttachmentEntityType, AttachmentFilterType, MultiProject, Project, Store } from "fond/types";
import { Actions, permissionCheck } from "fond/utils/permissions";
import { AgGrid, NonIdealState } from "fond/widgets";
import {
  dateValueFormatter,
  fileSizeValueFormatter,
  LinkCellRenderer,
  MimeTypeCellRenderer,
  stringComparator,
  userValueFormatter,
} from "fond/widgets/AgGrid";
import DataGridToolbar from "fond/widgets/DataGrid/DataGridToolbar";
import { ModalCloseHandler } from "fond/widgets/Modal";

import { DeleteAttachmentModal } from "../DeleteAttachmentModal";
import { useGetAttachmentRows } from "../hooks/useGetAttachmentRows";
import { RenameAttachmentModal } from "../RenameAttachmentModal";
import { AttachmentActionEventSource } from "../types";

import AttachmentListToolbarActions from "./AttachmentListToolbarActions";

import { AttachmentListContainer } from "./AttachmentList.styles";

export type AttachmentListProps = {
  /**
   * The current project being viewed
   */
  project: Project | MultiProject;
  /**
   * The level at which the attachment will be performed
   */
  attachmentEntityType?: AttachmentEntityType;
  /**
   * The unique identifier for a feature belonging to a layer on the map
   */
  featureId?: string | null;
  /**
   * The type of attachment to display
   */
  type?: AttachmentFilterType;
};

const AttachmentList: React.FC<AttachmentListProps> = ({ project, attachmentEntityType = "Project", featureId, type }: AttachmentListProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch: AppThunkDispatch = useDispatch();
  const currentUsername = useSelector((state: Store) => state.cognito.user?.username);
  const [selectedItems, setSelectedItems] = useState<Attachment[]>([]);
  const [renameAttachment, setRenameAttachment] = useState<Attachment | null>(null);
  const [deleteAttachments, setDeleteAttachments] = useState<Attachment[] | null>(null);
  const [getRows, items, totalFiles] = useGetAttachmentRows(project.ID, featureId, type);
  const layerConfigs = useSelector((state: Store) => selectAllVersionLayers(state, state.project.versionId));
  const layerKeymap = useMemo(() => {
    const map = new Map<string, string>();
    layerConfigs.forEach((layer) => map.set(layer.Key, layer.Label));
    return map;
  }, [layerConfigs]);
  const gridApi = useRef<GridApi>();

  const baseAnalyticsProps = useMemo(() => {
    const props: AttachmentBaseEventProps = {
      context: attachmentEntityType,
      location: "modal",
      projectId: project.ID,
    };
    if (attachmentEntityType === "Project" && type) {
      props.filterType = type;
    }
    if (attachmentEntityType === "Feature" && featureId) {
      props.featureId = featureId;
    }
    return props;
  }, [attachmentEntityType, featureId, project.ID, type]);

  /**
   * These are the toolbar action handlers. These actions are trigger from the datagrid selection toolbar.
   */

  /**
   * Open the 'View' url in a new tab for display in browser.
   */
  const handleOpenAttachment = (attachment: Attachment) => {
    if (attachment && attachment.Urls?.View) {
      analytics.track("attachments.action.open.clicked", baseAnalyticsProps);
      const newWindow = window.open(attachment.Urls.View, "_blank", "noopener,noreferrer");
      if (newWindow) newWindow.opener = null;
    }
  };

  /**
   * Initiate the download of multiple attachments.
   */
  const handleDownloadAttachments = useCallback(
    async (attachments: Attachment[], source: AttachmentActionEventSource = "toolbar") => {
      analytics.track("attachments.action.download.clicked", {
        ...baseAnalyticsProps,
        source,
        totalFiles,
        count: attachments.length,
      });
      return dispatch(downloadAttachments(attachments)).catch(() => enqueueSnackbar("Some attachments failed to download."));
    },
    [baseAnalyticsProps, dispatch, enqueueSnackbar, totalFiles]
  );

  /**
   * Create and open the delete confirmation model. On confirm dispatch an action to delete multiple attachments.
   */
  const handleDeleteAttachments = (attachments: Attachment[]) => {
    setDeleteAttachments(attachments);
    analytics.track("attachments.action.delete.clicked", {
      ...baseAnalyticsProps,
      totalFiles,
      count: attachments.length,
    });
  };

  const handleCloseDeleteAttachments: ModalCloseHandler = (_, reason) => {
    const analyticsProps = {
      ...baseAnalyticsProps,
      totalFiles,
      count: deleteAttachments?.length,
    };
    if (reason === "complete") {
      gridApi.current?.applyServerSideTransactionAsync({
        remove: deleteAttachments!,
      });
      analytics.track("attachments.files.deleted", analyticsProps);
      setSelectedItems([]);
    } else {
      analytics.track("attachments.files.delete.cancelled", analyticsProps);
    }
    setDeleteAttachments(null);
  };

  /**
   * Create and open the rename model. Dispatch an action to update attachment name.
   */
  const handleRenameAttachment = (attachment: Attachment) => {
    setRenameAttachment(attachment);
    analytics.track("attachments.action.rename.clicked", baseAnalyticsProps);
  };

  const handleCloseRenameAttachments: ModalCloseHandler<string> = (_, reason, name) => {
    if (reason === "complete") {
      gridApi.current?.applyServerSideTransactionAsync({
        update: [{ ...renameAttachment!, Name: name }],
      });
      analytics.track("attachments.file.renamed", { ...baseAnalyticsProps, changed: renameAttachment?.Name !== name });
    } else {
      analytics.track("attachments.file.rename.cancelled", baseAnalyticsProps);
    }
    setRenameAttachment(null);
  };

  /**
   * The data grid configuration.
   */

  /**
   * Set the 'selectedItems' state variable from the IDs of the currently selected row items.
   */
  const handleOnSelection = (event: SelectionChangedEvent) => {
    const selected: Attachment[] = [];
    event.api.forEachNode((node) => {
      if (node.isSelected()) {
        selected.push(node.data);
      }
    });
    setSelectedItems(selected);
  };

  const handleModelUpdated = (event: ModelUpdatedEvent) => {
    if (event.api.getDisplayedRowCount()) {
      event.api.hideOverlay();
      event.api.forEachNode((node: IRowNode) => {
        const selectNode = selectedItems.some((row) => {
          return row.ID === node.data?.ID;
        });

        if (selectNode) {
          node.setSelected(true, false);
        }
      });
    } else {
      event.api.showNoRowsOverlay();
    }
  };

  const handleOnGridReady = ({ api }: GridReadyEvent) => {
    gridApi.current = api;
  };

  const deleteAttachmentAction = attachmentEntityType === "Project" ? Actions.PROJECT_DELETE_ATTACHMENT : Actions.FEATURE_DELETE_ATTACHMENT;
  const editAttachmentAction = attachmentEntityType === "Project" ? Actions.PROJECT_EDIT_ATTACHMENT : Actions.FEATURE_EDIT_ATTACHMENT;
  const uploadAttachmentAction = attachmentEntityType === "Project" ? Actions.PROJECT_UPLOAD_ATTACHMENT : Actions.FEATURE_UPLOAD_ATTACHMENT;

  const noRowsOverlayComponent = useCallback(
    () => (
      <NonIdealState
        data-testid="attachment-list-empty-state-component"
        icon={<PlaylistAdd />}
        title={`This ${attachmentEntityType} Has No Attachments Yet.`}
        description={permissionCheck(project.Permission.Level, uploadAttachmentAction) ? "Upload a file to get started." : ""}
      />
    ),
    [attachmentEntityType, project.Permission.Level, uploadAttachmentAction]
  );

  const columns: ColDef[] = useMemo(
    () => [
      {
        field: "MimeType",
        headerName: "Type",
        minWidth: 55,
        width: 55,
        suppressHeaderMenuButton: true,
        cellRenderer: MimeTypeCellRenderer,
        cellClass: "vertically-align",
        resizable: false,
      },
      {
        field: "Name",
        headerName: "Name",
        minWidth: 100,
        flex: 1,
        sort: "asc",
        suppressHeaderMenuButton: true,
        valueGetter: (params: ValueGetterParams) => `${params.data.Name}.${params.data.Extension}`,
        cellRenderer: LinkCellRenderer,
        cellRendererParams: {
          onClick: (attachment: Attachment) => () => {
            handleDownloadAttachments([attachment], "list");
          },
        },
        comparator: stringComparator,
      },
      {
        field: "Size",
        headerName: "Size",
        valueFormatter: fileSizeValueFormatter,
        width: 80,
        type: "rightAligned",
        suppressHeaderMenuButton: true,
      },
      {
        field: "LayerFeature.Layer.LayerKey",
        headerName: "Layer",
        minWidth: 50,
        flex: 1,
        suppressHeaderMenuButton: true,
        valueFormatter: ({ data }: ValueFormatterParams<Attachment>) => layerKeymap.get(data?.LayerFeature?.Layer?.LayerKey ?? "") ?? "-",
        hide: type !== "layer_feature",
      },
      {
        field: "UploadedAt",
        headerName: "Uploaded At",
        valueFormatter: dateValueFormatter,
        width: 150,
        suppressHeaderMenuButton: true,
      },
      {
        field: "UploadedBy",
        headerName: "Uploaded By",
        valueFormatter: userValueFormatter(currentUsername),
        suppressHeaderMenuButton: true,
        flex: 1,
      },
    ],
    [currentUsername, handleDownloadAttachments, type, layerKeymap]
  );

  const gridOptions: GridOptions = useMemo(
    () => ({
      animateRows: true,
      noRowsOverlayComponent,
      rowModelType: "serverSide",
      cacheBlockSize: DEFAULT_PAGE_LIMIT,
      serverSideDatasource: { getRows },
      rowGroupPanelShow: "never",
      rowSelection: {
        mode: "multiRow",
        checkboxes: true,
      },
      getRowId: ({ data }) => data.ID,
      sideBar: false,
      suppressMovableColumns: true,
    }),
    [getRows, noRowsOverlayComponent]
  );

  return (
    <AttachmentListContainer data-testid="attachment-modal-list">
      {renameAttachment != null && (
        <RenameAttachmentModal attachment={renameAttachment} items={items} onClose={handleCloseRenameAttachments} validateUniqueName />
      )}
      {deleteAttachments != null && <DeleteAttachmentModal attachments={deleteAttachments} onClose={handleCloseDeleteAttachments} />}
      <Box sx={{ pl: 2, pr: 1 }}>
        <DataGridToolbar
          selected={selectedItems?.length}
          title={
            items?.length === 0
              ? `This is where you can upload and share ${attachmentEntityType} attachments.`
              : "Select one or more files to download or delete."
          }
          actions={
            <AttachmentListToolbarActions
              data-testid="attachment-list-datagrid-toolbar-actions"
              selectedItems={selectedItems}
              onOpen={handleOpenAttachment}
              onDownload={handleDownloadAttachments}
              onEdit={permissionCheck(project.Permission.Level, editAttachmentAction) ? handleRenameAttachment : undefined}
              onDelete={permissionCheck(project.Permission.Level, deleteAttachmentAction) ? handleDeleteAttachments : undefined}
              attachmentEntityType={attachmentEntityType}
            />
          }
          size="small"
        />
      </Box>
      <Box flexGrow={1}>
        <AgGrid
          columnDefs={columns}
          gridOptions={gridOptions}
          onSelectionChanged={handleOnSelection}
          onModelUpdated={handleModelUpdated}
          onGridReady={handleOnGridReady}
          variant="outlined"
        />
      </Box>
    </AttachmentListContainer>
  );
};

export default AttachmentList;
